/**
 * `AudienceAvatar` — Persona/audience avatar built on the monks-ui v4
 * Avatar primitives.
 *
 * Resolves a persona's `photoAssetId` to a download URL via
 * `useGetDownloadUrl`, then composes `<AvatarImage>` + `<AvatarFallback>`
 * so the photo renders when available and falls back to the initial of
 * the persona's name otherwise.
 *
 * Overlaps with `PersonaAvatar` in `shared/ui/`, which sizes in pixels
 * rather than on the Avatar size scale. The two are worth folding together.
 *
 * `photoAssetId` is intentionally optional: pass `undefined` (or omit it)
 * and the component renders the initial-only fallback that v4's Avatar
 * already paints when no AvatarImage src loads.
 */
import { Avatar, AvatarFallback, AvatarImage } from '@monksflow/monks-ui';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';

/**
 * Mirror the monks-ui v4 Avatar size/roundness contracts so consumers get the
 * same autocomplete and TS narrowing. Kept as local aliases (instead of
 * re-exporting from the DS) so a future v4 minor that adds variants doesn't
 * silently widen our prop surface — we update this file and the tests in
 * lockstep.
 */
type AvatarSize = 'xs' | 'mini' | 'sm' | 'default';
type AvatarRoundness = 'rounded' | 'full';

export interface AudienceAvatarProps {
  /** Persona name — first character drives the fallback initial. */
  name: string;
  /** Asset id of the persona's photo. When omitted, only the fallback renders. */
  photoAssetId?: string;
  size?: AvatarSize;
  roundness?: AvatarRoundness;
}

export function AudienceAvatar(props: AudienceAvatarProps) {
  const { name, photoAssetId, size = 'default', roundness = 'full' } = props;
  const { downloadUrl } = useGetDownloadUrl(photoAssetId);

  const initial = name.length > 0 ? name[0].toUpperCase() : '?';

  return (
    <Avatar size={size} roundness={roundness}>
      {downloadUrl ? <AvatarImage src={downloadUrl} alt={name} /> : null}
      <AvatarFallback size={size} roundness={roundness}>
        {initial}
      </AvatarFallback>
    </Avatar>
  );
}

AudienceAvatar.displayName = 'AudienceAvatar';
